import { TYPES } from '../render/constants';
import { addTextStyleAction, autoSelection } from './highlight';
import { Action } from './action';

export const copyTextAction = new Action({
  name: 'Copy',
  handler: copyActionHandler,
  command: 'Ctrl+C',
});

export const duplicateTextAction = new Action({
  name: 'Duplicate',
  handler: duplicateActionHandler,
  command: 'Ctrl+D',
});

export const cutTextAction = new Action({
  name: 'Cut',
  handler: cutActionHandler,
  command: 'Ctrl+X',
});

export const pasteTextAction = new Action({
  name: 'Paste',
  handler: pasteActionHandler,
  command: 'Ctrl+V',
});

export const deleteTextAction = new Action({
  name: 'Delete',
  handler: deleteActionHandler,
});

export const boldTextAction = new Action({
  name: 'Bold',
  handler: boldActionHandler,
  command: 'Ctrl+B',
  shortcuts: 'Ctrl+B',
});

function boldActionHandler(context) {
  const textNodes = context.api.getSelectedNodes().filter((node) => node.type === TYPES.text);
  if (!textNodes.length) return;

  const current = getCurrentFontWeight(context);
  const value = current === 'bold' ? 'normal' : 'bold';
  if (context.providers.text.currentNode) {
    addTextStyleAction(context, {
      fontWeight: value,
    });
  } else {
    textNodes.forEach((node) => {
      context.api.updateNode(node.id, {
        fontWeight: value,
      });
    });
  }
}

export const italicTextAction = new Action({
  name: 'Italic',
  handler: italicActionHandler,
  command: 'Ctrl+I',
  shortcuts: 'Ctrl+I',
});

function italicActionHandler(context) {
  const textNodes = context.api.getSelectedNodes().filter((node) => node.type === TYPES.text);
  if (!textNodes.length) return;

  const current = getCurrentFontStyle(context);
  const value = current === 'italic' ? 'normal' : 'italic';
  if (context.providers.text.currentNode) {
    addTextStyleAction(context, {
      fontStyle: value,
    });
  } else {
    textNodes.forEach((node) => {
      context.api.updateNode(node.id, {
        fontStyle: value,
      });
    });
  }
}

function getCurrentFontStyle(context) {
  const textProvider = context.providers.text;
  const currentNode = textProvider.currentNode;
  const textNodes = context.api.getSelectedNodes().filter((node) => node.type === TYPES.text);
  if (currentNode) {
    const styles = currentNode.styles || [];
    const currentSelection = autoSelection(context, styles);
    if (currentSelection && styles.length > 0) {
      const overlappingStyles = styles.filter(
        (style) =>
          style.selection[0] < currentSelection[1] &&
          style.selection[1] > currentSelection[0] &&
          style.fontStyle,
      );
      if (overlappingStyles.length > 0) {
        return overlappingStyles[0].fontStyle;
      }
    }
    return currentNode.fontStyle || 'normal';
  }
  const nodes = textNodes;
  if (!nodes.length) return 'normal';
  const first = nodes[0].fontStyle || 'normal';
  const allSame = nodes.every((node) => (node.fontStyle || 'normal') === first);
  return allSame ? first : 'normal';
}

function getCurrentFontWeight(context) {
  const textProvider = context.providers.text;
  const currentNode = textProvider.currentNode;
  const textNodes = context.api.getSelectedNodes().filter((node) => node.type === TYPES.text);
  // If we have a current text node being edited
  if (currentNode) {
    const styles = currentNode.styles || [];
    const currentSelection = autoSelection(context, styles);

    if (currentSelection && styles.length > 0) {
      // Find styles that intersect with current selection
      const overlappingStyles = styles.filter(
        (style) =>
          style.selection[0] < currentSelection[1] &&
          style.selection[1] > currentSelection[0] &&
          style.fontWeight,
      );

      if (overlappingStyles.length > 0) {
        // Return the font weight of the first overlapping style
        return overlappingStyles[0].fontWeight;
      }
    }

    // Fall back to node's base font weight
    return currentNode.fontWeight || 'normal';
  }

  // Default behavior for multiple selected nodes
  const nodes = textNodes;
  if (!nodes.length) return 'normal';

  const firstWeight = nodes[0].fontWeight || 'normal';
  const allSameWeight = nodes.every((node) => (node.fontWeight || 'normal') === firstWeight);

  return allSameWeight ? firstWeight : 'normal';
}

function copyActionHandler(context) {
  const { text } = context.providers;
  text.copy();
}

function pasteActionHandler(context) {
  const { text } = context.providers;
  text.paste();
}

function deleteActionHandler(context) {
  const { text } = context.providers;
  text.deleteText(-1);
}

function duplicateActionHandler(context) {
  const { text } = context.providers;
  text.copy();
  text.paste();
}

function cutActionHandler(context) {
  const { text } = context.providers;
  text.cut();
}
